import {
	ButtonHTMLAttributes,
	CSSProperties,
	createContext,
	forwardRef,
	HTMLAttributes,
	useContext,
	useEffect,
} from "react";
import { useIntl } from "react-intl";
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import classNames from "classnames/bind";
import { Direction } from "radix-ui";
import { usePrefersReducedMotion } from "app/hooks";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ArrowLeftIcon, ArrowRightIcon } from "app/icons/navigation";
import { getDotsWindow } from "./getDotsWindow";
import { useCarouselState } from "./useCarouselState";
import { CarouselSlideChange, useSlideChange } from "./useSlideChange";
import styles from "./Carousel.module.scss";

const cx = classNames.bind(styles);

type CssLength = number | string;

/** Numbers are pixels; strings are used as-is (rem, em, %...). */
const toCssLength = (length: CssLength) => (typeof length === "number" ? `${length}px` : length);

const carouselVariables = (
	slidesPerView: number | undefined,
	gap: CssLength | undefined,
	bleed: CssLength | undefined,
	clipMarginBlock: CssLength | undefined
) =>
	({
		"--carousel-slides-per-view": slidesPerView,
		"--carousel-gap": gap === undefined ? undefined : toCssLength(gap),
		"--carousel-bleed": bleed === undefined ? undefined : toCssLength(bleed),
		"--carousel-clip-margin-block":
			clipMarginBlock === undefined ? undefined : toCssLength(clipMarginBlock),
	}) as CSSProperties;

const DEFAULT_OPTIONS: EmblaOptionsType = { duration: 18, loop: true };
const DEFAULT_MAX_DOTS = 5;

export type CarouselApi = EmblaCarouselType;

type CarouselContextValue = {
	viewportRef: UseEmblaCarouselType[0];
	api: CarouselApi | undefined;
	scrollPrev: () => void;
	scrollNext: () => void;
	scrollTo: (index: number) => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
	selectedIndex: number;
	scrollSnaps: number[];
};

const CarouselContext = createContext<CarouselContextValue | null>(null);

export const useCarousel = () => {
	const context = useContext(CarouselContext);

	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel>");
	}

	return context;
};

export type CarouselProps = {
	/** Embla options. Pass `skipSnaps: true` for card lists, so a strong swipe moves several cards while still stopping on one. */
	opts?: EmblaOptionsType;
	plugins?: EmblaPluginType[];
	setApi?: (api: CarouselApi) => void;
	/** Fires when the selected slide changes, with its index, the snap total and what triggered it (arrow, dot, swipe or api). Meant for analytics. */
	onSlideChange?: (change: CarouselSlideChange) => void;
	/** How many slides are visible at once, fractions allowed (1.4 shows a peek of the next one). Defaults to 1. */
	slidesPerView?: number;
	/** Space between slides. A number is pixels, a string any CSS length (e.g. "1.5rem"). */
	gap?: CssLength;
	/** How far slides overflow the carousel on both sides while scrolling, typically the page padding so they reach the screen edges. Slides then snap to the start of the carousel, and `loop` is turned off since Embla would recycle slides still visible in the bleed. */
	bleed?: CssLength;
	/** Room above and below the slides before they get clipped, so shadows, focus rings or hover effects show in full. The layout does not move. */
	clipMarginBlock?: CssLength;
	/** Fills the height of its parent, down to the slides: for a carousel in a box sized by its surroundings. The parent needs a height. */
	fill?: boolean;
	/** "region" makes the carousel a landmark: only for a standalone carousel that is a major area of the page (e.g. a hero). Keep "group" inside a titled section or a card. */
	role?: "group" | "region";
} & Omit<HTMLAttributes<HTMLDivElement>, "role">;

/**
 * Accessible carousel built on Embla. Compose it with `CarouselContent`, `CarouselItem`
 * and the optional controls. It loops by default (`opts={{ loop: false }}` to disable),
 * unless it has a `bleed`. Arrows and dots jump without animation when the user prefers
 * reduced motion. Name it with `aria-label`, or `aria-labelledby` pointing to the section heading.
 */
export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
	(
		{
			opts,
			plugins,
			setApi,
			onSlideChange,
			slidesPerView,
			gap,
			bleed,
			clipMarginBlock,
			fill = false,
			role = "group",
			className,
			style,
			children,
			...rest
		},
		ref
	) => {
		const direction = Direction.useDirection();
		const prefersReducedMotion = usePrefersReducedMotion();
		const hasBleed = bleed !== undefined && bleed !== 0;
		const [viewportRef, api] = useEmblaCarousel(
			{
				...DEFAULT_OPTIONS,
				direction,
				...(hasBleed && { align: "start" }),
				...opts,
				...(hasBleed && { loop: false }),
				...(prefersReducedMotion && { duration: 0 }),
			},
			plugins
		);
		const state = useCarouselState(api);
		const { markSource } = useSlideChange(api, onSlideChange);

		useEffect(() => {
			if (api && setApi) {
				setApi(api);
			}
		}, [api, setApi]);

		const scrollPrev = () => {
			markSource("arrow");
			api?.scrollPrev();
		};

		const scrollNext = () => {
			markSource("arrow");
			api?.scrollNext();
		};

		const scrollTo = (index: number) => {
			markSource("dot");
			api?.scrollTo(index);
		};

		return (
			<CarouselContext.Provider
				value={{ viewportRef, api, scrollPrev, scrollNext, scrollTo, ...state }}
			>
				<div
					ref={ref}
					className={cx("carousel", className)}
					data-fill={fill || undefined}
					style={{
						...carouselVariables(slidesPerView, gap, bleed, clipMarginBlock),
						...style,
					}}
					role={role}
					aria-roledescription="carousel"
					{...rest}
				>
					{children}
				</div>
			</CarouselContext.Provider>
		);
	}
);
Carousel.displayName = "Carousel";

export const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
	({ className, ...rest }, ref) => {
		return (
			<div
				ref={ref}
				className={cx("carousel__item", className)}
				role="group"
				aria-roledescription="slide"
				{...rest}
			/>
		);
	}
);
CarouselItem.displayName = "CarouselItem";

export type CarouselArrowProps = {
	/** "shadow" is a white button with a drop shadow (for use over images), "outline" a bordered transparent one. */
	variant?: "shadow" | "outline";
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const CarouselPrevious = forwardRef<HTMLButtonElement, CarouselArrowProps>(
	({ variant = "shadow", className, ...rest }, ref) => {
		const { scrollPrev, canScrollPrev } = useCarousel();
		const { formatMessage } = useIntl();

		const handleClick = () => {
			if (canScrollPrev) {
				scrollPrev();
			}
		};

		return (
			<button
				ref={ref}
				type="button"
				className={cx("carousel__arrow", className)}
				data-direction="prev"
				data-variant={variant}
				data-testid="carousel-previous"
				onClick={handleClick}
				aria-disabled={!canScrollPrev || undefined}
				aria-label={formatMessage({ id: "carousel.previous" })}
				{...rest}
			>
				<ArrowLeftIcon />
			</button>
		);
	}
);
CarouselPrevious.displayName = "CarouselPrevious";

export const CarouselNext = forwardRef<HTMLButtonElement, CarouselArrowProps>(
	({ variant = "shadow", className, ...rest }, ref) => {
		const { scrollNext, canScrollNext } = useCarousel();
		const { formatMessage } = useIntl();

		const handleClick = () => {
			if (canScrollNext) {
				scrollNext();
			}
		};

		return (
			<button
				ref={ref}
				type="button"
				className={cx("carousel__arrow", className)}
				data-direction="next"
				data-variant={variant}
				data-testid="carousel-next"
				onClick={handleClick}
				aria-disabled={!canScrollNext || undefined}
				aria-label={formatMessage({ id: "carousel.next" })}
				{...rest}
			>
				<ArrowRightIcon />
			</button>
		);
	}
);
CarouselNext.displayName = "CarouselNext";

export type CarouselContentProps = {
	/** Renders the previous/next arrows over the slides. "overlay-hover" only reveals them on hover/focus, and keeps them visible on devices that cannot hover. Omit to place `CarouselPrevious` / `CarouselNext` yourself. */
	arrows?: "overlay" | "overlay-hover";
} & HTMLAttributes<HTMLDivElement>;

export const CarouselContent = forwardRef<HTMLDivElement, CarouselContentProps>(
	({ arrows, className, ...rest }, ref) => {
		const { viewportRef } = useCarousel();

		return (
			<div className={cx("carousel__content")} data-arrows={arrows}>
				<div ref={viewportRef} className={cx("carousel__viewport")}>
					<div ref={ref} className={cx("carousel__track", className)} {...rest} />
				</div>
				{arrows && (
					<>
						<CarouselPrevious />
						<CarouselNext />
					</>
				)}
			</div>
		);
	}
);
CarouselContent.displayName = "CarouselContent";

export type CarouselDotsProps = {
	/** "white" (default) for use over images: a white current dot among translucent white ones. "active" for a plain background: a current dot in the brand active color among grey ones. */
	variant?: "white" | "active";
	/** Maximum number of dots shown at once. Beyond it, a window follows the current slide. */
	maxDots?: number;
	/** Off by default: the dots are a plain position indicator, not focusable and hidden from assistive technologies, as arrows and swipe already navigate. Turn it on when jumping to a slide matters; each dot then takes at least 24px to stay a large enough touch target. */
	interactive?: boolean;
	/** Diameter of a dot, and height of the current one. A number is pixels. Defaults to 8px. */
	size?: CssLength;
	/** Space between two dots. A number is pixels. Defaults to 8px. */
	gap?: CssLength;
	/** Length of the current dot. A number is pixels. Defaults to 16px. */
	activeWidth?: CssLength;
} & HTMLAttributes<HTMLDivElement>;

const dotsVariables = (
	size: CssLength | undefined,
	gap: CssLength | undefined,
	activeWidth: CssLength | undefined
) =>
	({
		"--carousel-dot-size": size === undefined ? undefined : toCssLength(size),
		"--carousel-dot-gap": gap === undefined ? undefined : toCssLength(gap),
		"--carousel-dot-active-width":
			activeWidth === undefined ? undefined : toCssLength(activeWidth),
	}) as CSSProperties;

/**
 * One dot per slide, the current one stretched into a pill. Size them with `size`, `gap` and
 * `activeWidth`, or from any parent with `--carousel-dot-size`, `--carousel-dot-gap` and `--carousel-dot-active-width`.
 */
export const CarouselDots = forwardRef<HTMLDivElement, CarouselDotsProps>(
	(
		{
			variant = "white",
			maxDots = DEFAULT_MAX_DOTS,
			interactive = false,
			size,
			gap,
			activeWidth,
			className,
			style,
			...rest
		},
		ref
	) => {
		const { scrollSnaps, selectedIndex, scrollTo } = useCarousel();
		const { formatMessage } = useIntl();
		const {
			start,
			size: windowSize,
			hasPrevious,
			hasNext,
		} = getDotsWindow(scrollSnaps.length, selectedIndex, maxDots);

		if (scrollSnaps.length <= 1) {
			return null;
		}

		const end = start + windowSize - 1;

		return (
			<div
				ref={ref}
				className={cx("carousel__dots", className)}
				style={{ ...dotsVariables(size, gap, activeWidth), ...style }}
				data-variant={variant}
				data-interactive={interactive}
				aria-hidden={interactive ? undefined : true}
				{...rest}
			>
				{scrollSnaps.map((_, index) => {
					const isHidden = index < start || index > end;
					const isEdge = (index === start && hasPrevious) || (index === end && hasNext);

					if (!interactive) {
						return (
							<span
								key={index}
								className={cx("carousel__dot")}
								data-edge={isEdge || undefined}
								data-hidden={isHidden || undefined}
								aria-current={index === selectedIndex}
							/>
						);
					}

					return (
						<button
							key={index}
							type="button"
							className={cx("carousel__dot")}
							data-edge={isEdge || undefined}
							aria-current={index === selectedIndex}
							data-hidden={isHidden || undefined}
							aria-hidden={isHidden || undefined}
							tabIndex={isHidden ? -1 : undefined}
							aria-label={formatMessage(
								{ id: "carousel.goToSlide" },
								{ number: index + 1 }
							)}
							onClick={() => scrollTo(index)}
						/>
					);
				})}
			</div>
		);
	}
);
CarouselDots.displayName = "CarouselDots";

export const CarouselCounter = forwardRef<
	HTMLParagraphElement,
	HTMLAttributes<HTMLParagraphElement>
>(({ className, ...rest }, ref) => {
	const { selectedIndex, scrollSnaps } = useCarousel();
	const { formatMessage } = useIntl();
	const current = selectedIndex + 1;
	const total = scrollSnaps.length;

	if (total <= 1) {
		return null;
	}

	return (
		<Typography
			ref={ref}
			component="p"
			variant={TYPOGRAPHY_VARIANTS.SMALL}
			className={cx("carousel__counter", className)}
			{...rest}
		>
			<span aria-hidden="true">
				{current} / {total}
			</span>
			<span className={cx("carousel__visually-hidden")}>
				{formatMessage({ id: "carousel.counter" }, { current, total })}
			</span>
		</Typography>
	);
});
CarouselCounter.displayName = "CarouselCounter";

export type { CarouselInteractionSource, CarouselSlideChange } from "./useSlideChange";
